// frontend/src/pages/admin/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../utils/axios';
import {
  FiShoppingBag,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiPackage,
  FiShoppingCart,
  FiAlertCircle,
  FiCheckCircle
} from 'react-icons/fi';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/api/dashboard/stats');
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Dashboard stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Дөнгөж сая';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} минутын өмнө`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} цагийн өмнө`;
    return `${Math.floor(seconds / 86400)} өдрийн өмнө`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner-lg"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Мэдээлэл ачаалагдсангүй</p>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Хянах самбар</h1>
        <p className="page-subtitle">Системийн ерөнхий мэдээлэл</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {/* Total Revenue */}
        <div className="stat-card">
          <div className="stat-header">
            <div>
              <p className="stat-label">Борлуулалт (энэ сар)</p>
              <h3 className="stat-value">
                ₮{(stats.totalRevenue / 1000000).toFixed(1)}М
              </h3>
            </div>
            <div className="stat-icon stat-icon-success">
              <FiDollarSign size={24} />
            </div>
          </div>
          <p className={`stat-change ${stats.revenueGrowth >= 0 ? 'stat-change-up' : 'stat-change-down'}`}>
            {stats.revenueGrowth >= 0 ? <FiTrendingUp className="inline" /> : <FiTrendingDown className="inline" />}
            {' '}{Math.abs(stats.revenueGrowth)}% өмнөх сартай харьцуулахад
          </p>
        </div>

        {/* Total Orders */}
        <div className="stat-card">
          <div className="stat-header">
            <div>
              <p className="stat-label">Захиалга (энэ сар)</p>
              <h3 className="stat-value">{stats.totalOrders}</h3>
            </div>
            <div className="stat-icon stat-icon-primary">
              <FiShoppingCart size={24} />
            </div>
          </div>
          <p className={`stat-change ${stats.newOrders >= 0 ? 'stat-change-up' : ''}`}>
            {stats.newOrders >= 0 ? '↑' : '↓'} {Math.abs(stats.newOrders)} өмнөх сартай харьцуулахад
          </p>
        </div>

        {/* Total Products */}
        <div className="stat-card">
          <div className="stat-header">
            <div>
              <p className="stat-label">Нийт бараа</p>
              <h3 className="stat-value">{stats.totalProducts}</h3>
            </div>
            <div className="stat-icon stat-icon-warning">
              <FiShoppingBag size={24} />
            </div>
          </div>
          <p className="stat-change">
            Бага үлдэгдэл: {stats.lowStock} • Дууссан: {stats.outOfStock}
          </p>
        </div>

        {/* Total Customers */}
        <div className="stat-card">
          <div className="stat-header">
            <div>
              <p className="stat-label">Харилцагчид</p>
              <h3 className="stat-value">{stats.totalCustomers}</h3>
            </div>
            <div className="stat-icon stat-icon-danger">
              <FiUsers size={24} />
            </div>
          </div>
          <p className="stat-change stat-change-up">
            ↑ {stats.newCustomers} шинэ (энэ сар)
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pending Orders */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FiAlertCircle className="text-yellow-600" />
              Хүлээгдэж буй захиалга
            </h3>
            <span className="badge badge-warning">{stats.pendingOrders}</span>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Таны баталгаажуулалт хүлээж байна
          </p>
          <Link to="/admin/orders?status=pending" className="btn btn-primary btn-sm">
            Захиалга харах
          </Link>
        </div>

        {/* Pending Payments */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FiCheckCircle className="text-blue-600" />
              Баталгаажуулах төлбөр
            </h3>
            <span className="badge badge-blue">{stats.pendingPayments}</span>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Банкны төлбөр шалгах шаардлагатай
          </p>
          <Link to="/admin/orders?payment_status=unpaid" className="btn btn-primary btn-sm">
            Төлбөр харах
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Сүүлийн үйл ажиллагаа
        </h3>
        <div className="space-y-3">
          {stats.recentActivities && stats.recentActivities.length > 0 ? (
            stats.recentActivities.map((activity) => (
              <Link
                key={`${activity.type}-${activity.id}`}
                to={activity.link}
                className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 -mx-4 px-4 rounded transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-xl">
                    {activity.icon}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {activity.title}
                    </p>
                    <p className="text-xs text-gray-600">{activity.description}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {getTimeAgo(activity.time)}
                    </p>
                  </div>
                </div>
                {activity.status && (
                  <span className={`badge ${
                    activity.status === 'pending' ? 'badge-warning' :
                    activity.status === 'completed' ? 'badge-success' :
                    'badge-info'
                  }`}>
                    {activity.status}
                  </span>
                )}
              </Link>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FiPackage size={48} className="mx-auto mb-3 opacity-50" />
              <p>Үйл ажиллагаа байхгүй</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;